<template>
  <div class="cqm-wrapper mb-4">
    <!-- Top row: 2 wide tiles (Chat + DND) — horizontal layout -->
    <div class="cqm-top-row">
      <button
        v-for="s in topItems"
        :key="s.id"
        @click="handleTileClick(s)"
        :class="['cqm-tile', 'cqm-tile--wide', { 'cqm-tile--dnd-active': s.id === 'dnd' && dnd, 'cqm-tile--disabled': s._disabled }]"
        type="button"
      >
        <div
          :class="['cqm-icon-wrap', { 'cqm-icon-wrap--active': s.id === 'dnd' && dnd }]"
          :style="s.id === 'dnd' && dnd ? {} : { color: s._iconColor, borderColor: s._borderColor }"
        >
          <component :is="s.icon" :size="18" :stroke-width="2" />
        </div>
        <div class="cqm-wide-text">
          <p class="cqm-label">{{ s.label }}</p>
          <p v-if="s.id === 'dnd'" class="cqm-hint">{{ dnd ? 'Active' : 'Off' }}</p>
          <p v-else-if="s._hint" class="cqm-hint">{{ s._hint }}</p>
        </div>
        <div v-if="s.id === 'dnd'" :class="['cqm-mini-toggle', { 'cqm-mini-toggle--on': dnd }]">
          <span class="cqm-mini-thumb"></span>
        </div>
        <i v-else class="simple-icon-arrow-right cqm-arrow"></i>
      </button>
    </div>

    <!-- Grid top section -->
    <div :class="['cqm-grid', gridTopClass]">
      <button
        v-for="s in gridTopItems"
        :key="s.id"
        @click="handleTileClick(s)"
        :class="['cqm-tile', { 'cqm-tile--disabled': s._disabled }]"
        type="button"
      >
        <div class="cqm-icon-wrap" :style="{ color: s._iconColor, borderColor: s._borderColor }">
          <component :is="s.icon" :size="20" :stroke-width="2" />
        </div>
        <p class="cqm-label">{{ s.label }}</p>
        <p v-if="s._hint" class="cqm-hint">{{ s._hint }}</p>
      </button>
    </div>

    <!-- Grid bottom section (remainder row, wider tiles) -->
    <div v-if="gridBottomItems.length > 0" :class="['cqm-grid', gridBottomClass]">
      <button
        v-for="s in gridBottomItems"
        :key="s.id"
        @click="handleTileClick(s)"
        :class="['cqm-tile', { 'cqm-tile--disabled': s._disabled }]"
        type="button"
      >
        <div class="cqm-icon-wrap" :style="{ color: s._iconColor, borderColor: s._borderColor }">
          <component :is="s.icon" :size="20" :stroke-width="2" />
        </div>
        <p class="cqm-label">{{ s.label }}</p>
        <p v-if="s._hint" class="cqm-hint">{{ s._hint }}</p>
      </button>
    </div>

    <!-- Full-width bottom tile (Tip, when Voucher is active) -->
    <button
      v-if="footerItem"
      @click="handleTileClick(footerItem)"
      :class="['cqm-tile', 'cqm-tile--wide', 'cqm-tile--footer', { 'cqm-tile--disabled': footerItem._disabled }]"
      type="button"
    >
      <div class="cqm-icon-wrap" :style="{ color: footerItem._iconColor, borderColor: footerItem._borderColor }">
        <component :is="footerItem.icon" :size="18" :stroke-width="2" />
      </div>
      <div class="cqm-wide-text">
        <p class="cqm-label">{{ footerItem.label }}</p>
        <p v-if="footerItem._hint" class="cqm-hint">{{ footerItem._hint }}</p>
      </div>
      <i class="simple-icon-arrow-right cqm-arrow"></i>
    </button>
  </div>
</template>

<script>
const HINTS = {
  chat:           'Message staff',
  housekeeping:   'Full clean',
  'report-issue': 'Something wrong?',
  dining:         'Order in-room',
  activity:       'Explore around',
  voucher:        'Deals & promos',
  tip:            'Send love',
  feedback:       'Rate your stay',
}

const ICON_COLORS = {
  chat:           '#2A8FA8',
  dnd:            '#7B52B8',
  housekeeping:   '#3A8A48',
  'report-issue': '#C04A30',
  dining:         '#C04A30',
  activity:       '#C07B20',
  voucher:        '#8B5CF6',
  tip:            '#C03060',
  feedback:       '#2B6FB8',
}

export default {
  name: 'ConciergeQuickMenu',

  props: {
    items: {
      type: Array,
      default: () => [],
      validator: items =>
        items.every(item => Boolean(item.id && item.label && item.icon)),
    },
    dnd: {
      type: Boolean,
      default: false,
    },
    reservationStatus: {
      type: String,
      default: '',
    },
  },

  computed: {
    disabledIds() {
      var status = this.reservationStatus
      var ids = {}
      var checkInOnly = ['dnd', 'housekeeping', 'report-issue', 'tip']
      checkInOnly.forEach(function(id) {
        if (status !== 'CHECKIN') ids[id] = true
      })
      if (status === 'NEW_RESERVATION') ids['feedback'] = true
      return ids
    },

    topItems() {
      return this.items.slice(0, 2).map(s => this.enrich(s))
    },
    gridItems() {
      return this.items.slice(2, 8).map(s => this.enrich(s))
    },
    gridTopItems() {
      var items = this.gridItems
      if (items.length <= 4) return items.slice(0, 2)
      return items.slice(0, 3)
    },
    gridBottomItems() {
      var items = this.gridItems
      if (items.length <= 4) return items.slice(2)
      return items.slice(3)
    },
    gridTopClass() {
      return this.gridItems.length <= 4 ? 'cqm-grid--2col' : ''
    },
    gridBottomClass() {
      var remaining = this.gridBottomItems.length
      if (remaining === 1) return 'cqm-grid--1col'
      if (remaining === 2) return 'cqm-grid--2col'
      return ''
    },
    footerItem() {
      var item = this.items[8]
      return item ? this.enrich(item) : null
    },
  },

  methods: {
    enrich(s) {
      const iconColor = ICON_COLORS[s.id] || '#1C2030'
      var hint = HINTS[s.id] || null
      if (s.id === 'feedback' && this.items[8] && this.items[8].id === 'feedback') hint = 'We\'d love to hear about your stay'
      return {
        ...s,
        _hint:        hint,
        _iconColor:   iconColor,
        _borderColor: iconColor + '40',
        _disabled:    Boolean(this.disabledIds[s.id]),
      }
    },

    handleTileClick(s) {
      if (s._disabled) {
        var msg = s.id === 'feedback'
          ? 'Available once your reservation is active'
          : 'Available after check-in'
        this.$emit('show-toast', msg)
        return
      }
      if (s.id === 'dnd') this.$emit('toggle-dnd')
      else this.$emit('select', s)
    },
  },
}
</script>

<style scoped>
.cqm-wrapper {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.cqm-top-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 9px;
}

.cqm-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 9px;
}

.cqm-grid--1col {
  grid-template-columns: 1fr;
}

.cqm-grid--2col {
  grid-template-columns: repeat(2, 1fr);
}

.cqm-tile {
  background: #FFFFFF;
  border: 1.5px solid transparent;
  border-radius: 16px;
  padding: 13px 4px 11px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(40, 45, 55, 0.05), 0 3px 8px rgba(40, 45, 55, 0.06);
  cursor: pointer;
  font-family: inherit;
  transition: transform 0.12s ease;
  -webkit-tap-highlight-color: transparent;
}

.cqm-tile--wide {
  flex-direction: row;
  align-items: center;
  padding: 12px 14px;
  gap: 10px;
}

.cqm-wide-text {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.cqm-tile--wide .cqm-label {
  text-align: left;
  margin-bottom: 1px;
}

.cqm-tile--wide .cqm-hint {
  text-align: left;
}

.cqm-arrow {
  font-size: 10px;
  color: #C0C4CF;
  flex-shrink: 0;
}

.cqm-tile:active {
  transform: scale(0.94);
  box-shadow: none;
}

.cqm-tile--dnd-active {
  background: #EEF4F7;
  border-color: var(--merchant-primary-color, #2B5F7A);
}

.cqm-icon-wrap {
  width: 42px;
  height: 42px;
  border-radius: 13px;
  background: #F3F4F6;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  color: #1C2030;
  transition: background 0.2s, color 0.2s;
}

.cqm-tile--wide .cqm-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 11px;
}

.cqm-tile--footer {
  width: 100%;
}

.cqm-icon-wrap--active {
  background: var(--merchant-primary-color, #2B5F7A);
  color: #fff;
  border-color: transparent;
}

.cqm-label {
  font-size: 10px;
  font-weight: 800;
  color: #1C2030;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  margin: 0;
  text-align: center;
  line-height: 1.2;
  position: relative;
  z-index: 1;
}

.cqm-hint {
  font-size: 9px;
  font-weight: 500;
  color: #888EA0;
  margin: 0;
  text-align: center;
  line-height: 1.25;
  position: relative;
  z-index: 1;
}

/* Mini toggle inside DND tile */
.cqm-mini-toggle {
  width: 34px;
  height: 18px;
  border-radius: 999px;
  background: #D0D5DD;
  display: flex;
  align-items: center;
  padding: 2px;
  transition: background 0.2s;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

.cqm-mini-toggle--on {
  background: var(--merchant-primary-color, #2B5F7A);
}

.cqm-mini-thumb {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.22);
  transition: margin-left 0.2s;
  flex-shrink: 0;
}

.cqm-mini-toggle--on .cqm-mini-thumb {
  margin-left: 16px;
}

.cqm-tile--disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/concierge/general/ConciergeQuickMenu.vue